// toast — a tiny dependency-free toast store. The back-office had no
// notification system (mutations surfaced success/failure only as inline
// text, or not at all), so this is the one place that owns transient
// "✓ saved" / "✗ failed" feedback for every action button.
//
// It's a framework-agnostic pub/sub store read by <Toaster/> through
// useSyncExternalStore, plus an imperative `toast.*` API that anything —
// including the react-query MutationCache in main.tsx — can call without a
// hook. Auto-dismiss, a small stack cap, and coalescing of identical
// messages (so a double-click doesn't stack two copies) are built in.

export type ToastKind = "success" | "error" | "info";

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  /** ms before auto-dismiss; 0 keeps it until dismissed. */
  duration: number;
}

// Errors linger a little longer than confirmations so operators can read
// what went wrong before it slides away.
const DEFAULT_DURATION: Record<ToastKind, number> = {
  success: 4000,
  info: 4500,
  error: 6500,
};

// At most this many visible at once — older ones drop off the top.
const MAX_VISIBLE = 4;

let seq = 0;
let toasts: Toast[] = [];
const listeners = new Set<() => void>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit() {
  for (const l of listeners) l();
}

function commit(next: Toast[]) {
  toasts = next;
  emit();
}

function clearTimer(id: number) {
  const t = timers.get(id);
  if (t) {
    clearTimeout(t);
    timers.delete(id);
  }
}

function scheduleDismiss(id: number, duration: number) {
  clearTimer(id);
  if (duration > 0) {
    timers.set(id, setTimeout(() => dismissToast(id), duration));
  }
}

/** Subscribe for useSyncExternalStore. Returns an unsubscribe fn. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Stable snapshot for useSyncExternalStore — identity only changes on edit. */
export function getSnapshot(): Toast[] {
  return toasts;
}

export function dismissToast(id: number) {
  clearTimer(id);
  if (toasts.some((t) => t.id === id)) {
    commit(toasts.filter((t) => t.id !== id));
  }
}

export function dismissAll() {
  for (const id of timers.keys()) clearTimeout(timers.get(id)!);
  timers.clear();
  if (toasts.length) commit([]);
}

function push(kind: ToastKind, message: string, opts?: { duration?: number }): number {
  const text = (message ?? "").toString().trim() || "Done.";
  const duration = opts?.duration ?? DEFAULT_DURATION[kind];

  // Coalesce duplicates: an identical message still showing just gets its
  // timer refreshed rather than stacking a second, identical card.
  const existing = toasts.find((t) => t.kind === kind && t.message === text);
  if (existing) {
    scheduleDismiss(existing.id, duration);
    return existing.id;
  }

  const id = ++seq;
  let next = [...toasts, { id, kind, message: text, duration }];
  if (next.length > MAX_VISIBLE) {
    for (const dropped of next.slice(0, next.length - MAX_VISIBLE)) {
      clearTimer(dropped.id);
    }
    next = next.slice(next.length - MAX_VISIBLE);
  }
  commit(next);
  scheduleDismiss(id, duration);
  return id;
}

export const toast = {
  success: (message: string, opts?: { duration?: number }) => push("success", message, opts),
  error: (message: string, opts?: { duration?: number }) => push("error", message, opts),
  info: (message: string, opts?: { duration?: number }) => push("info", message, opts),
  dismiss: dismissToast,
  dismissAll,
};
